/**
 * Re-implements scroll-behavior: smooth, but in JS, to avoid defect
 *
 * There's a browser behaviour that causes anchor link transitions
 * to re-position the contents of an overflow div, in an effort
 * to ensure the scrolled element is visible.
 *
 * Example:
 * https://stackoverflow.com/questions/61211356/anchor-link-scrolls-into-overflow-content-of-overflow-hidden
 *
 * This behaviour is causing us issues, so this component
 * intervenes and performs a smooth scroll without the behaviour
 *
 */

import { userPreferences } from "../../utils/userPreferences";

const smoothScroll = () => {
  // Helper function to perform the actual smooth scrolling
  const scrollToElement = (selector: string): void => {
    const $scrollTo = document.querySelector(selector);

    if (!$scrollTo) {
      return;
    }

    window.scroll({
      behavior: userPreferences.prefersReducedMotion ? "auto" : "smooth",
      left: 0,
      top: $scrollTo.getBoundingClientRect().top + window.scrollY,
    });
  };

  // Handle anchor link clicks
  document.querySelectorAll("[href^=\"#\"]").forEach(($el: Element) => {
    const $htmlEl = $el as HTMLAnchorElement;
    const selector = $htmlEl.getAttribute("href");

    if (!selector || selector === "#") {
      return;
    }

    $htmlEl.addEventListener("click", (e: Event) => {
      e.preventDefault();

      // Update URL without page reload
      history.pushState(null, "", selector);

      // Perform smooth scroll
      scrollToElement(selector);
    });
  });

  // Handle initial page load with hash in URL
  const handleUrlHash = (): void => {
    const { hash } = window.location;

    if (hash && hash !== "#") {
      // Small timeout to ensure DOM is fully loaded
      // Especially important for dynamically loaded content
      setTimeout(() => {
        scrollToElement(hash);
      }, 100);
    }
  };

  // Call initially
  handleUrlHash();

  // Also handle hash changes without page reload
  window.addEventListener("hashchange", () => {
    handleUrlHash();
  });
};

export default smoothScroll;
